/**
 * カート追加フォームのビジネスロジック（純粋関数）
 *
 * DOM操作・副作用を含まない。Vitestでテスト可能。
 * v1 互換: assets/product-form.js:241-282 の prepareCartUpdateData / getCurrentLineItemProperties。
 */

/** /cart.js が返す line item の最小フィールド */
export interface CartItem {
  variant_id: number;
  quantity: number;
  properties?: Record<string, string> | null;
  /** 販売プラン割当（#3485）。selling_plan ありで投入したラインのみ持つ */
  selling_plan_allocation?: { selling_plan?: { id?: number } } | null;
}

/** /cart.js のレスポンス（必要部分のみ） */
export interface CartState {
  items: CartItem[];
}

/** 既存ライン検出結果（1-indexed line 番号） */
export interface ExistingLine {
  /** Shopify Cart API の line 番号（1-indexed） */
  line: number;
  /** 既存ラインの数量 */
  quantity: number;
}

/** /cart/add リクエストの body 構造 */
export interface CartAddPayload {
  id: number;
  quantity: number;
  properties: Record<string, string>;
  sections_url: string;
  /** 販売プラン ID（#3485）。requires_selling_plan=true の商品は必須 */
  selling_plan?: number;
}

/** /cart/change リクエストの body 構造 */
export interface CartChangePayload {
  line: number;
  quantity: number;
  properties: Record<string, string>;
  sections_url: string;
  /** 販売プラン ID（#3485）。同一 variant でも別ラインとなるため change 側でも保持 */
  selling_plan?: number;
}

/** Cart Ajax API のエラーレスポンス形（必要部分のみ） */
export interface CartApiErrorResponse {
  status?: number;
  description?: string;
  message?: string;
}

/**
 * カートに えらんでBOX（pantry）が含まれるかを判定する（submit 時のリアルタイム再確認用）。
 *
 * BOX 親・子とも `_pantryKey` line item property を持つため、その有無で判定する。
 * server 側の `data-cart-has-pantry` はページロード時点のスナップショットのため、
 * 別タブで BOX を追加した後に元タブで追加操作した場合など、submit 時点で陳腐化しうる。
 * onSubmit で /cart.js をフェッチした値を本関数で再確認し、混在ブロックを補完する。
 */
export function cartHasPantryBox(cart: CartState): boolean {
  return cart.items.some(
    (item) => item.properties != null && item.properties['_pantryKey'] != null,
  );
}

/**
 * カート内の既存ラインから同一バリアント + 同一販売プランを 1-indexed line 番号付きで返す。
 *
 * v1 product-form.js:242,249 と等価:
 *   - `variant_id == formData.id`（== による型寛容比較を踏襲し number/string 混在に耐える）
 *   - 見つかったインデックス + 1 を line 番号として返す
 *
 * #3485: selling_plan を指定したカート追加は Shopify 側で「同一 variant でも別ライン」
 * として扱われるため、selling_plan ID まで一致したラインのみ既存扱いとする。
 * 未指定（通常購入）の場合は selling_plan_allocation が無いラインのみが対象。
 */
export function findExistingLine(
  cart: CartState,
  variantId: number,
  sellingPlanId?: number,
): ExistingLine | null {
  const index = cart.items.findIndex((item) => {
    if (item.variant_id != variantId) return false;
    const itemPlan = item.selling_plan_allocation?.selling_plan?.id;
    return (itemPlan ?? undefined) === (sellingPlanId ?? undefined);
  });
  if (index === -1) return null;
  return { line: index + 1, quantity: cart.items[index].quantity };
}

/** 既存カート数 + 新規入力数 */
export function computeTotalQuantity(
  existingQty: number,
  newQty: number,
): number {
  return existingQty + newQty;
}

/** 寄付金額 = 単価 × 合計数量（v1 product-form.js:244 互換） */
export function computeTotalDonation(
  perUnit: number,
  totalQuantity: number,
): number {
  return perUnit * totalQuantity;
}

/** properties 抽出に必要な input の最小インターフェイス（テスト容易化のため HTMLInputElement に依存しない） */
export interface PropertyInputLike {
  name: string;
  value: string;
  type: string;
  checked: boolean;
}

/**
 * `name="properties[...]"` 形式の hidden input 群から
 * properties オブジェクトを抽出する。
 *
 * v1 product-form.js:261-282 互換:
 *   - radio/checkbox は checked かつ value !== '' のもののみ採用
 *   - その他は value !== '' のみ採用
 *   - 同一キーは後勝ち
 */
export function extractAnyGiftProperties(
  inputs: readonly PropertyInputLike[],
): Record<string, string> {
  const properties: Record<string, string> = {};
  inputs.forEach((element) => {
    if (element.type === 'radio' || element.type === 'checkbox') {
      if (!element.checked) return;
    }
    if (element.value === '') return;
    const key = element.name.replace(/^properties\[/, '').replace(/\]$/, '');
    properties[key] = element.value;
  });
  return properties;
}

/**
 * properties オブジェクトを合成する。
 *
 * v1 product-form.js:251-256 互換: anyGift を先に展開し、
 * 固定キー（支援先団体・支援金額・賞味期限）で上書きする。
 *
 * 内容量（K9: product.metafields.product.volume）は Checkout 明細に表示するため
 * line item property 化する（CO-04）。product 単位で variant 不変。
 * 空（volume 未設定の旧商品など）のときは付与しない（「内容量: 」の空行を出さない）。
 */
export function mergeProperties(input: {
  anyGift: Record<string, string>;
  supportGroup: string;
  totalDonation: number;
  expirationDate: string;
  /** 内容量（product.metafields.product.volume）。空なら property を付与しない */
  volume?: string;
  supportGroupKey: string;
  supportAmountKey: string;
}): Record<string, string> {
  const properties: Record<string, string> = {
    ...input.anyGift,
    [input.supportGroupKey]: input.supportGroup,
    [input.supportAmountKey]: input.totalDonation.toString(),
    賞味期限: input.expirationDate,
  };
  if (input.volume) {
    properties['内容量'] = input.volume;
  }
  return properties;
}

/** /cart/add に POST する body を組み立てる */
export function buildAddPayload(input: {
  variantId: number;
  quantity: number;
  properties: Record<string, string>;
  sectionsUrl: string;
  sellingPlan?: number;
}): CartAddPayload {
  const payload: CartAddPayload = {
    id: input.variantId,
    quantity: input.quantity,
    properties: input.properties,
    sections_url: input.sectionsUrl,
  };
  if (input.sellingPlan !== undefined) {
    payload.selling_plan = input.sellingPlan;
  }
  return payload;
}

/** /cart/change に POST する body を組み立てる */
export function buildChangePayload(input: {
  line: number;
  totalQuantity: number;
  properties: Record<string, string>;
  sectionsUrl: string;
  sellingPlan?: number;
}): CartChangePayload {
  const payload: CartChangePayload = {
    line: input.line,
    quantity: input.totalQuantity,
    properties: input.properties,
    sections_url: input.sectionsUrl,
  };
  if (input.sellingPlan !== undefined) {
    payload.selling_plan = input.sellingPlan;
  }
  return payload;
}

/**
 * Cart Ajax API のエラーレスポンスからユーザー表示用メッセージを抽出する。
 *
 * v1 product-form.js:304-306 互換: `description` を優先。
 */
export function parseCartApiError(
  response: CartApiErrorResponse,
  fallback: string,
): string {
  return response.description || response.message || fallback;
}
